import * as Sentry from '@sentry/react'
import Cookies from 'universal-cookie'
import type {
	CaptureErrorOptions,
	SentryUserContext
} from './sentryHandler.types'

/**
 * Extract response and status from error object
 */
const getErrorResponse = (
	error: unknown
): { data: unknown; status?: number } => {
	if (error && typeof error === 'object' && 'response' in error) {
		const { response } = error as {
			response?: { data?: unknown; status?: number }
		}
		return {
			data: response?.data,
			status: response?.status
		}
	}
	return { data: error, status: undefined }
}

/**
 * Set user context in Sentry for error tracking
 * @param user - User information to track
 * @param user.userId - User ID (redundant for compatibility)
 * @param user.name - User name (optional)
 */
export const setSentryUser = (user: SentryUserContext): void => {
	Sentry.setUser({
		userId: String(user.userId),
		username: user.name
	})
}

/**
 * Clear user context from Sentry (useful on logout)
 */
export const clearSentryUser = (): void => {
	Sentry.setUser(null)
}

/**
 * Initialize Sentry user from cookies if available
 * Validates and sets user context from cookies containing userId and fullName
 */
export const initializeSentryUserFromCookies = (): void => {
	const cookies = new Cookies()
	const userId = cookies.get('userId')
	const userName = cookies.get('fullName')

	if (userId && userName) {
		setSentryUser({
			id: userId,
			userId,
			name: userName
		})
	}
}

/**
 * Capture and report errors to Sentry with contextual information
 * @param options - Configuration options for error capture
 * @param options.section - The section/module where the error occurred
 * @param options.event - The specific event or action that caused the error
 * @param options.error - The error object to capture
 * @param options.breadcrumb - Breadcrumb information for error context
 * @param options.tags - Additional tags for error categorization
 * @param options.extra - Extra data to attach to the error report
 */
export const captureErrorSentry = (options: CaptureErrorOptions): void => {
	try {
		Sentry.setTag('section', options?.section as string)
		Sentry.setTag('event', options?.event as string)

		if (options?.breadcrumb) {
			Sentry.addBreadcrumb({
				category: options.breadcrumb?.category,
				message: options.breadcrumb?.message,
				level: options.breadcrumb?.level ?? 'info',
				data: options.breadcrumb?.data
			})
		}

		if (options?.tags && Object.keys(options.tags).length > 0) {
			Object.entries(options.tags).forEach(([key, value]) => {
				Sentry.setTag(key, value)
			})
		}

		let response = options?.extra?.response
		let status = options?.extra?.status

		if (options?.error && (!response || status === undefined)) {
			const errorResponse = getErrorResponse(options.error)
			response = response ?? errorResponse.data
			status = status ?? errorResponse.status
		}

		Sentry.captureException(new Error(options.section), {
			extra: {
				payload: options?.extra?.payload,
				params: options?.extra?.params,
				response,
				status,
				...options?.extra
			}
		})
	} catch (err) {
		console.error('Error capturing exception in Sentry', err)
	}
}
